import { inject, Injectable } from '@angular/core';
import { LocalStorageService } from '@app/core/services/local-storage/local-storage.service';
import { Observable, of } from 'rxjs';
import { IPosition } from '@app/core/interfaces/position.interface';
import { isEnvFeatureEnabled } from '@app/core/env-feature/env-feature';
import { EnvType } from 'src/environments/common/environment-type.enum';
import { UserType } from '@app/core/enums/user-type.enum';
import { userMeMockData } from '@mock/data/user-me-mock.data';

export const MOCK_ENVS: EnvType[] = [EnvType.Development, EnvType.Test, EnvType.Stage];
export const MOCK_ENABLED_LS_KEY = 'mock_enabled';

@Injectable({
  providedIn: 'root',
})
export class MockService {
  private localStorageService: LocalStorageService = inject(LocalStorageService);

  public readonly LS_Enabled_Key: string = MOCK_ENABLED_LS_KEY;
  public readonly LS_i18n_Enabled_Key: string = 'mock_i18n_enabled';
  public readonly LS_Data_Generated_Key: string = 'mock_data_generated';
  public readonly LS_Console_Enabled_Key: string = 'mock_console_enabled';
  public readonly LS_User_Type_Key: string = 'mock_user_type';
  public readonly LS_Request_Delay_Key: string = 'mock_request_delay';
  public readonly LS_Pipe_Segments_Key: string = 'mock_pipe_segments';

  public mapCenter: IPosition = { lat: 51.066280530873605, lon: 10.320209583461875 };
  public readonly defaultRequestDelay = 100;

  protected get requestDelay(): number {
    return this.getRequestDelay();
  }

  public readonly dataGeneratedCount = {
    projects: 50,
    areas: 500,
    userReportAreasSelections: 20,
    pipesNetworks: {
      segments: 50000, // the default of "Mock pipe segments": a city, towns and villages joined by mains
      // how far segments merge into pipes — an average, not exact: a street pipe runs 50–150% of it in blocks
      averageBlocksPerPipe: 7,
      configs: 80, // every config of the project, the styled ones among them
      styledPipeEvery: 50, // every Nth pipe draws with a config carrying a line color, width and opacity; 0 for none
    },
    // the volumes measured in GC-703 (loggers / correlations and events): 1000 / 100, 3000 / 300 (the largest projects
    // seen), 5000 / 500 (the ceiling)
    loggerDeploymentsData: 300,
    loggerMeasurements: 300, // of the last measurement day, one per logger — what Leak detection shows; at most loggerDeploymentsData
    alphaDeploymentsData: 40,
    repeaterDeploymentsData: 20,
    correlations: 30,
    events: 30,
  };

  public isMockEnabled(): boolean {
    if (isEnvFeatureEnabled(MOCK_ENVS)) {
      return this.localStorageService.getFromLS(this.LS_Enabled_Key) === true;
    }

    return false;
  }

  public toggleMockEnabled(): void {
    this.localStorageService.saveToLS(this.LS_Enabled_Key, !this.isMockEnabled());
  }

  public isI18nMockEnabled(): boolean {
    if (this.isMockEnabled()) {
      return this.localStorageService.getFromLS(this.LS_i18n_Enabled_Key) === true;
    }

    return false;
  }

  public toggleI18nMock(): void {
    this.localStorageService.saveToLS(this.LS_i18n_Enabled_Key, !this.isI18nMockEnabled());
  }

  public isMockDataGenerated(): boolean {
    if (this.isMockEnabled()) {
      return this.localStorageService.getFromLS(this.LS_Data_Generated_Key) === true;
    }

    return false;
  }

  public toggleMockDataGenerated(): void {
    this.localStorageService.saveToLS(this.LS_Data_Generated_Key, !this.isMockDataGenerated());
  }

  public isMockConsoleEnabled(): boolean {
    if (this.isMockEnabled()) {
      return this.localStorageService.getFromLS(this.LS_Console_Enabled_Key) === true;
    }

    return false;
  }

  public toggleMockConsoleEnabled(): void {
    this.localStorageService.saveToLS(this.LS_Console_Enabled_Key, !this.isMockConsoleEnabled());
  }

  public getUserType(): UserType {
    return this.localStorageService.getFromLS<UserType>(this.LS_User_Type_Key) ?? userMeMockData.type;
  }

  public setUserType(type: UserType): void {
    this.localStorageService.saveToLS(this.LS_User_Type_Key, type);
  }

  public getRequestDelay(): number {
    return this.localStorageService.getFromLS<number>(this.LS_Request_Delay_Key) ?? this.defaultRequestDelay;
  }

  public setRequestDelay(ms: number | null): void {
    if (ms === null || !Number.isFinite(ms)) {
      this.localStorageService.removeFromLS(this.LS_Request_Delay_Key);
      return;
    }

    this.localStorageService.saveToLS(this.LS_Request_Delay_Key, Math.max(0, ms));
  }

  public getPipeSegments(): number {
    return (
      this.localStorageService.getFromLS<number>(this.LS_Pipe_Segments_Key) ??
      this.dataGeneratedCount.pipesNetworks.segments
    );
  }

  public setPipeSegments(segments: number | null): void {
    if (segments === null || !Number.isFinite(segments)) {
      this.localStorageService.removeFromLS(this.LS_Pipe_Segments_Key);
      return;
    }

    this.localStorageService.saveToLS(this.LS_Pipe_Segments_Key, Math.max(1, segments));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected mockResponse<T>(name: string, response: T, request?: any): Observable<T> {
    if (this.isMockConsoleEnabled())
      console.info('\x1b[33m%s\x1b[0m', `[MOCK]`, `${name}: `, 'Response:', response, 'Request:', request);

    return of(response);
  }
}
